import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import api from '../services/api';
import PageSkeleton from '../components/Skeleton';

const GOLD = '#B8892A';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';

const TIERS = [
  { key: 'gold', labelKey: 'membership.tierGoldSeller', threshold: 0, color: '#D4A843', icon: '💛' },
  { key: 'platinum', labelKey: 'membership.tierPlatinumSeller', threshold: 10000, color: '#8B9DC3', icon: '💎' },
  { key: 'diamond', labelKey: 'membership.tierDiamondSeller', threshold: 50000, color: '#9B59B6', icon: '👑' },
];

const BENEFITS = [
  { icon: '💰', labelKey: 'membership.sellerBenefitCommission', descKey: 'membership.sellerBenefitCommissionDesc', unlockTier: 'gold',
    detailKeys: { gold: 'membership.commission12', platinum: 'membership.commission8', diamond: 'membership.commission5' } },
  { icon: '🚀', labelKey: 'membership.sellerBenefitPriority', descKey: 'membership.sellerBenefitPriorityDesc', unlockTier: 'platinum' },
  { icon: '📸', labelKey: 'membership.sellerBenefitPhoto', descKey: 'membership.sellerBenefitPhotoDesc', unlockTier: 'platinum',
    detailKeys: { platinum: 'membership.photo3Monthly', diamond: 'membership.photoUnlimited' } },
  { icon: '🏷', labelKey: 'membership.sellerBenefitFeatured', descKey: 'membership.sellerBenefitFeaturedDesc', unlockTier: 'platinum',
    detailKeys: { platinum: 'membership.featured1Monthly', diamond: 'membership.featured3Monthly' } },
  { icon: '👤', labelKey: 'membership.sellerBenefitConsultant', descKey: 'membership.sellerBenefitConsultantDesc', unlockTier: 'diamond' },
  { icon: '🎯', labelKey: 'membership.sellerBenefitPricing', descKey: 'membership.sellerBenefitPricingDesc', unlockTier: 'diamond' },
];

function tierIndex(key) {
  return TIERS.findIndex(t => t.key === key);
}

export default function SellerMembership() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('benefits'); // benefits | rules
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    api.get('/customers/my-center').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <PageSkeleton />;

  const growth = Number(data?.seller_growth || 0);
  const currentTier = data?.seller_tier || 'gold';
  const ci = tierIndex(currentTier);
  const tier = TIERS[ci] || TIERS[0];
  const nextTier = TIERS[ci + 1] || null;
  const neededGrowth = nextTier ? nextTier.threshold - growth : 0;
  const progress = nextTier ? Math.min(100, ((growth - tier.threshold) / (nextTier.threshold - tier.threshold)) * 100) : 100;

  const FAQ = [
    {
      q: t('membership.faqSellerGrowthQ'),
      a: t('membership.faqSellerGrowthA'),
    },
    {
      q: t('membership.faqSellerBenefitsQ'),
      a: t('membership.faqSellerBenefitsA'),
    },
    {
      q: t('membership.faqCommissionQ'),
      a: t('membership.faqCommissionA'),
    },
  ];

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 40 }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '14px 16px',
          background: '#fff', borderBottom: `1px solid ${BD}`,
        }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: INK }}>←</button>
          <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 16, color: INK }}>{t('membership.seller')}</span>
          <button onClick={() => navigate('/membership/rules')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: MUTED }}>{t('membership.rules')}</button>
        </div>

        {/* Tier Card */}
        <div style={{
          margin: '16px 16px 0', borderRadius: 14, overflow: 'hidden',
          background: 'linear-gradient(135deg, #2A1A0A 0%, #3A2A1A 50%, #2A1A0A 100%)',
          padding: '28px 24px', position: 'relative',
        }}>
          {/* Decorative */}
          <div style={{
            position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)',
            width: 80, height: 80, borderRadius: '50%',
            border: '2px solid rgba(255,255,255,.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              width: 50, height: 50, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(212,168,67,.2), transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28,
            }}>{tier.icon}</div>
          </div>

          <div style={{ fontSize: 20, fontWeight: 700, color: tier.color, marginBottom: 2 }}>
            {t(tier.labelKey)}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', marginBottom: 12 }}>
            {t('membership.totalGrowth')} <span style={{ color: GOLD, fontWeight: 600, fontSize: 16 }}>{growth.toLocaleString()}</span>
          </div>

          {/* Progress bar */}
          {nextTier && (
            <div>
              <div style={{
                height: 6, borderRadius: 3, background: 'rgba(255,255,255,.1)',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%', borderRadius: 3, width: `${progress}%`,
                  background: `linear-gradient(90deg, ${tier.color}, ${nextTier.color})`,
                  transition: 'width .5s ease',
                }} />
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between', marginTop: 6,
                fontSize: 10, color: 'rgba(255,255,255,.4)',
              }}>
                <span>{t(tier.labelKey)}</span>
                <span>{t('membership.upgrade')} {neededGrowth.toLocaleString()} → {t(nextTier.labelKey)}</span>
              </div>
            </div>
          )}
          {!nextTier && (
            <div style={{
              padding: '4px 12px', borderRadius: 4, fontSize: 11,
              background: 'rgba(212,168,67,.2)', color: GOLD, display: 'inline-block',
            }}>{t('membership.maxTierReached')}</div>
          )}
        </div>

        {/* Tab Switch */}
        <div style={{
          display: 'flex', margin: '20px 16px 0', gap: 0,
          background: '#fff', borderRadius: 8, overflow: 'hidden',
          border: `1px solid ${BD}`,
        }}>
          {[
            { key: 'benefits', label: t('membership.benefits') },
            { key: 'rules', label: t('membership.rules') },
          ].map(item => (
            <button key={item.key} onClick={() => setTab(item.key)} style={{
              flex: 1, padding: '12px 0', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: tab === item.key ? 600 : 400,
              color: tab === item.key ? GOLD : MUTED,
              background: tab === item.key ? `${GOLD}08` : 'transparent',
              borderBottom: tab === item.key ? `2px solid ${GOLD}` : '2px solid transparent',
            }}>{item.label}</button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'benefits' && (
          <div style={{ margin: '16px 16px 0' }}>
            {BENEFITS.map(b => {
              const unlocked = tierIndex(b.unlockTier) <= ci;
              return (
                <div key={b.labelKey} style={{
                  background: '#fff', borderRadius: 10, marginBottom: 10,
                  border: `1px solid ${BD}`,
                  padding: '16px', display: 'flex', gap: 14, alignItems: 'flex-start',
                  opacity: unlocked ? 1 : 0.5,
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                    background: unlocked ? '#FDF6E3' : '#F5F5F5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20,
                  }}>{b.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 2 }}>
                      {t(b.labelKey)}
                      {!unlocked && (
                        <span style={{
                          marginLeft: 8, fontSize: 10, padding: '1px 6px',
                          borderRadius: 3, background: '#F0F0F0', color: '#999',
                        }}>{t(TIERS[tierIndex(b.unlockTier)]?.labelKey)}{t('membership.upgrade')}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: MUTED }}>{t(b.descKey)}</div>
                    {b.detailKeys && unlocked && (
                      <div style={{
                        marginTop: 6, fontSize: 11, color: GOLD,
                        padding: '4px 8px', background: `${GOLD}08`, borderRadius: 4,
                        display: 'inline-block',
                      }}>
                        {t('membership.currentLevel')}: {b.detailKeys[currentTier] ? t(b.detailKeys[currentTier]) : '—'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === 'rules' && (
          <div style={{ margin: '16px 16px 0' }}>
            {/* Tier comparison table */}
            <div style={{
              background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
              overflow: 'hidden', marginBottom: 16,
            }}>
              <div style={{
                padding: '14px 16px', borderBottom: `1px solid ${BD}`,
                fontSize: 14, fontWeight: 600, color: INK,
              }}>{t('membership.rules')}</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#FAFAFA' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', color: MUTED, fontWeight: 500 }}>{t('membership.currentLevel')}</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', color: MUTED, fontWeight: 500 }}>{t('membership.upgrade')}</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', color: MUTED, fontWeight: 500 }}>{t('membership.feeDiscount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {TIERS.map((tier, i) => (
                    <tr key={tier.key} style={{
                      borderTop: `1px solid ${BD}20`,
                      background: tier.key === currentTier ? `${GOLD}06` : 'transparent',
                    }}>
                      <td style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{tier.icon}</span>
                        <span style={{ fontWeight: tier.key === currentTier ? 600 : 400, color: tier.key === currentTier ? GOLD : INK }}>
                          {t(tier.labelKey)}
                        </span>
                        {tier.key === currentTier && (
                          <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 2, background: GOLD, color: '#fff' }}>{t('membership.currentLevel')}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: MUTED }}>
                        {tier.threshold > 0 ? `≥${tier.threshold.toLocaleString()}` : '—'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: INK, fontWeight: 500 }}>
                        {['12%', '8%', '5%'][i]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* FAQ */}
            {FAQ.map((faq, i) => (
              <div key={i} style={{
                background: '#fff', borderRadius: 8, marginBottom: 10,
                border: `1px solid ${BD}`,
              }}>
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} style={{
                  width: '100%', padding: '14px 16px', background: 'none', border: 'none',
                  cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>{faq.q}</span>
                  <span style={{ color: MUTED, fontSize: 12 }}>{expandedFaq === i ? t('common.close') : t('common.more')} ›</span>
                </button>
                {expandedFaq === i && (
                  <div style={{ padding: '0 16px 14px', fontSize: 13, color: MUTED, lineHeight: 1.7 }}>
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
